'use client';

import { type ProductFilterSchema, productFilterSchema } from '@coas/validators/payload';
import { type ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react';
import { Label, Slider, SliderOutput, SliderThumb, SliderTrack } from 'react-aria-components';
import { Controller, type ControllerRenderProps, type UseFormReturn } from 'react-hook-form';
import { Price } from '~/shared/ui/price';

type FilterPriceProps = {
    form: UseFormReturn<ProductFilterSchema>;
};

const initialValues = productFilterSchema.parse({});
const [_MIN_PRICE = 0, MAX_PRICE = 0] = initialValues.price ?? [];

const toFormValue = (_value: number) => {
    const zeroToOneRange = _value / MAX_PRICE;
    const exp = Math.ceil(zeroToOneRange ** 3 * MAX_PRICE);

    if (exp < 1000) return Math.round(exp / 50) * 50;
    if (exp < 5_000) return Math.round(exp / 100) * 100;
    if (exp < 15_000) return Math.round(exp / 500) * 500;
    return Math.round(exp / 1000) * 1000;
};

const fromFormValue = (value: number) => {
    const zeroToOneRange = value / MAX_PRICE;
    return Math.ceil(Math.cbrt(zeroToOneRange) * MAX_PRICE);
};

type SliderFieldProps = Omit<ControllerRenderProps<{ price: number[] }, 'price'>, 'ref'>;

const ControlledSlider = forwardRef<HTMLDivElement, SliderFieldProps>((field, ref: ForwardedRef<HTMLDivElement>) => {
    const [value, setValue] = useState<number[]>(initialValues.price);

    const onChange = useCallback(
        (_value: number[]) => {
            // setValue(_value);
            field.onChange(_value.map(toFormValue));
        },
        [field.onChange],
    );

    useEffect(() => {
        setValue(field.value.map(fromFormValue));
    }, [field.value]);

    return (
        <>
            <Slider
                ref={ref}
                minValue={initialValues.price[0]}
                maxValue={initialValues.price[1]}
                step={100}
                value={value}
                onChange={onChange}
                isDisabled={field.disabled}
                className={'space-y-2'}
            >
                <Label slot='label' className='sr-only'>
                    Диапазон цены
                </Label>

                <SliderOutput className={'flex justify-between text-18 text-neutral-500'}>
                    {field.value.map((_value, index) => (
                        <Price key={index} withoutCurrency>
                            {_value}
                        </Price>
                    ))}
                </SliderOutput>
                <SliderTrack className={'h-1 bg-neutral-200'}>
                    {({ state }) =>
                        state.values.map((_, i) => (
                            <SliderThumb key={i} index={i} className={'mt-0.5 h-5 w-5 rounded-full bg-neutral-950'} />
                        ))
                    }
                </SliderTrack>
            </Slider>
        </>
    );
});

export default ({ form }: FilterPriceProps) => {
    const { control } = form;

    return (
        <>
            <div className={'flex w-full flex-col px-page'}>
                <Controller name={'price'} control={control} render={({ field }) => <ControlledSlider {...field} />} />
            </div>
        </>
    );
};
